import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Shield, 
  Lock, 
  Eye, 
  FileText, 
  Building2,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';

const Section = ({ icon: Icon, number, title, children }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-lime/10 flex items-center justify-center flex-shrink-0">
          {Icon ? <Icon className="w-5 h-5 text-lime" /> : <span className="text-lime font-bold">{number}</span>}
        </div>
        <div className="space-y-3">
          <h2 className="text-xl font-semibold">{title}</h2>
          <div className="text-muted-foreground space-y-3">
            {children}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const TermsOfServicePage = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="terms-of-service-page">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime/10 mb-4">
          <FileText className="w-8 h-8 text-lime" />
        </div>
        <h1 className="text-3xl font-bold mb-3">Terms of Service</h1>
        <p className="text-muted-foreground">Last updated: February 2025</p>
      </div>

      <Alert className="border-amber-500 bg-amber-50">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Important:</strong> This application is for internal use only and is not intended for use by Microsoft Partners or customers.
        </AlertDescription>
      </Alert>

      <div className="space-y-6">
        <Section icon={Building2} title="1. Purpose and Scope">
          <p>
            Skilltrac ("the Application") is a Training Delivery Management System developed and maintained by Technofocus Pte Ltd. ("Technofocus") exclusively for Microsoft Corporation ("Microsoft") and its internal Project Management team.
          </p>
          <p>
            <strong>This Application is strictly for internal use only.</strong> It is designed to help Microsoft's internal teams manage, track, and oversee training delivery programs for Microsoft Partners.
          </p>
          <p>The Application is not intended for direct use by:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Microsoft Partners</li>
            <li>Microsoft Customers</li>
            <li>External third parties</li>
            <li>Any individual not authorized by Microsoft</li>
          </ul>
        </Section>

        <Section icon={Eye} title="2. Data Collection and Usage">
          <p>The Application collects and stores information necessary for training delivery management, including:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Partner organization information</li>
            <li>Training project details and schedules</li>
            <li>Session information and attendance records</li>
            <li>Feedback and evaluation data</li>
            <li>User account information (name, email, role)</li>
            <li>Purchase order and financial tracking data</li>
          </ul>
          <p className="font-medium text-foreground mt-4">
            All information, including Personally Identifiable Information (PII) and business data saved in this Application:
          </p>
          <ul className="list-disc list-inside ml-4">
            <li>Is intended solely for Microsoft's internal use</li>
            <li>Is accessible to Technofocus as the Application administrator</li>
            <li>Will be used exclusively for training delivery management</li>
            <li>Will not be shared with unauthorized third parties</li>
          </ul>
        </Section>

        <Section icon={Shield} title="3. Data Governance and Privacy">
          <p>Technofocus is committed to protecting the data stored within this Application. As the Application manager, Technofocus:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Follows its Privacy Policy to handle and govern the use of all data</li>
            <li>Uses data only for Microsoft's internal training management purposes</li>
            <li>Implements appropriate security measures to protect data integrity</li>
            <li>Maintains data confidentiality in accordance with industry standards</li>
          </ul>
          <p className="mt-4">For complete details on how Technofocus handles data, please refer to our Privacy Policy:</p>
          <a 
            href="https://technofocus.ai/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-lime hover:underline mt-2"
          >
            technofocus.ai/privacy
            <ExternalLink className="w-4 h-4" />
          </a>
        </Section>

        <Section icon={Lock} title="4. Marketing and Communications Restrictions">
          <p><strong>Technofocus explicitly commits that:</strong></p>
          <ul className="list-disc list-inside ml-4">
            <li>Data collected through this Application will <strong>NOT</strong> be used for any marketing purposes</li>
            <li>User information will <strong>NOT</strong> be used for promotional email communications</li>
            <li>Partner or training data will <strong>NOT</strong> be shared for advertising or marketing campaigns</li>
            <li>No commercial solicitations will be made based on data from this Application</li>
          </ul>
          <p className="mt-4">Communications related to this Application will be limited to:</p>
          <ul className="list-disc list-inside ml-4">
            <li>System notifications and alerts</li>
            <li>Technical support communications</li>
            <li>Service updates and maintenance notices</li>
            <li>Security-related announcements</li>
          </ul>
        </Section>

        <Section number="5" title="5. Access and Authorization">
          <p>Access to this Application is restricted to authorized Technofocus users only. Users must:</p>
          <ul className="list-disc list-inside ml-4">
            <li>Be employed by or contracted with Technofocus</li>
            <li>Have a valid Technofocus email account (technofocus.in or technofocus.co)</li>
            <li>Be approved by an administrator before accessing the system</li>
            <li>Use the Application only for its intended purpose of managing training requests</li>
          </ul>
          
          <p className="mt-4 font-medium text-foreground">User Roles and Responsibilities:</p>
          <ul className="list-disc list-inside ml-4">
            <li><strong>Super Admin</strong> - Full system access including user management, settings, audit logs, and the ability to promote users to Admin or Super Admin</li>
            <li><strong>Admin</strong> - Manage projects, users, POs, approve user registrations, and configure settings</li>
            <li><strong>Project Manager (PM)</strong> - Manage assigned training projects and sessions, submit feedback</li>
          </ul>
          
          <p className="mt-4">
            Unauthorized access or misuse of this Application may result in immediate termination of access and may be subject to legal action.
          </p>
        </Section>

        <Section number="6" title="6. Audit and Compliance">
          <p>To ensure compliance and accountability, this Application maintains comprehensive audit logs that record:</p>
          <ul className="list-disc list-inside ml-4">
            <li>User account approvals and denials</li>
            <li>Role changes (promotions and demotions)</li>
            <li>Account status changes (activation/deactivation)</li>
            <li>Domain whitelist modifications</li>
            <li>System setting changes</li>
          </ul>
          <p className="mt-4">
            Audit logs are accessible only to Super Admins and Admins. All logged actions include timestamps, actor information, and action details for complete traceability.
          </p>
        </Section>

        <Section number="7" title="7. Contact Information">
          <p>For questions or concerns regarding these Terms of Service or data handling practices, please contact:</p>
          <div className="bg-muted/50 rounded-lg p-4 mt-4">
            <p className="font-medium text-foreground">Technofocus Pte Ltd.</p>
            <p className="mt-2">
              Email: <a href="mailto:contact@technofocus.ai" className="text-lime hover:underline">contact@technofocus.ai</a>
            </p>
            <p>
              Website: <a href="https://technofocus.ai" target="_blank" rel="noopener noreferrer" className="text-lime hover:underline">technofocus.ai</a>
            </p>
          </div>
        </Section>
      </div>

      <Card className="bg-secondary/5 border-secondary/20">
        <CardContent className="pt-6">
          <div className="text-center text-sm text-muted-foreground">
            <p>
              By accessing and using this Application, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsOfServicePage;
